import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ExternalLink, Globe2, Newspaper } from "lucide-react";
import { SEO } from "@/components/nyla/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intelJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nylaairwater.earth/" },
    { "@type": "ListItem", position: 2, name: "Water Intelligence", item: "https://nylaairwater.earth/water-intelligence" },
  ],
};

const fmt = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return d;
  }
};

export default function WaterIntelligence() {
  const [items, setItems] = useState(null);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    axios
      .get(`${API}/water-news`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, []);

  const categories = items ? ["all", ...new Set(items.map((i) => i.category).filter(Boolean))] : ["all"];
  const shown = !items ? [] : category === "all" ? items : items.filter((i) => i.category === category);

  return (
    <div data-testid="water-intelligence-page" className="pt-14">
      <SEO
        title="Water Intelligence — Daily Water & Sustainability News"
        description="A daily brief on the water world — atmospheric water generation, water scarcity, sustainability, regulation, and industry innovation, curated by Nyla Air Water."
        path="/water-intelligence"
        keywords="water news, water industry intelligence, atmospheric water generation news, water scarcity India, water sustainability news, bottled water industry"
        jsonLd={intelJsonLd}
      />

      <section className="py-20 md:py-28 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl"
          >
            <p className="text-xs md:text-sm tracking-[0.2em] uppercase font-semibold text-[#86868B] mb-6">
              Daily Brief
            </p>
            <h1 data-testid="intel-heading" className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight font-bold text-[#1D1D1F]">
              Water Intelligence.
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-[#86868B]">
              What's moving in the world of water — scarcity, sustainability, technology, regulation,
              and the future of how we drink. Curated daily by the Nyla team.
            </p>
          </motion.div>

          {categories.length > 2 && (
            <div className="mt-10 flex items-center gap-2 flex-wrap">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`intel-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition-colors duration-200 ${
                    category === c
                      ? "bg-[#1D1D1F] text-white"
                      : "bg-white text-[#1D1D1F]/70 hover:bg-black/5 border border-[#D2D2D7]/60"
                  }`}
                >
                  {c === "all" ? "All stories" : c}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {!items ? (
            <p className="text-sm text-[#86868B] text-center py-20">Loading the latest brief…</p>
          ) : shown.length === 0 ? (
            <div data-testid="intel-empty" className="text-center py-20">
              <Newspaper className="w-8 h-8 text-[#86868B] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-base text-[#86868B]">
                Today's brief is being prepared. Check back soon.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {shown.map((item, i) => (
                <motion.article
                  key={item.id}
                  data-testid={`intel-card-${item.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                  className="bg-[#F5F5F7] rounded-3xl p-7 md:p-9"
                >
                  <div className="flex items-center gap-3 flex-wrap mb-4">
                    <span className="text-[10px] tracking-[0.15em] uppercase font-semibold px-3 py-1 rounded-full bg-[#2C3E35]/10 text-[#2C3E35]">
                      {item.category}
                    </span>
                    {item.region && (
                      <span className="flex items-center gap-1 text-xs text-[#86868B]">
                        <Globe2 className="w-3.5 h-3.5" /> {item.region}
                      </span>
                    )}
                    <span className="text-xs text-[#86868B]">{fmt(item.published_date)}</span>
                  </div>

                  <h2 className="font-display text-2xl md:text-3xl tracking-tight font-bold text-[#1D1D1F] leading-snug mb-4">
                    {item.headline}
                  </h2>

                  <div className="space-y-3">
                    {(item.summary || []).map((p, j) => (
                      <p key={j} className="text-[15px] leading-relaxed text-[#1D1D1F]/80">{p}</p>
                    ))}
                  </div>

                  {item.why_it_matters && (
                    <div className="mt-5 border-l-2 border-[#2C3E35] bg-white rounded-r-2xl px-5 py-4">
                      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2C3E35] mb-1.5">
                        Why it matters
                      </p>
                      <p className="text-sm leading-relaxed text-[#1D1D1F]/80">{item.why_it_matters}</p>
                    </div>
                  )}

                  <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
                    <p className="text-xs text-[#86868B]">
                      Source: <span className="font-medium text-[#1D1D1F]/70">{item.source_name}</span>
                    </p>
                    <a
                      href={item.source_url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      data-testid={`intel-source-link-${item.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-[#2C3E35] hover:underline"
                    >
                      Read original <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
